import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useSearchParams } from "react-router-dom"
import { Col, Flex, Grid, Modal, Row, Tag, Typography } from "antd"
import { useTranslation } from "react-i18next"
import DataTable from "ui/DataTable"
import Widget from "components/accounting/Widget"
import TipsDetails from "components/accounting/TipsDetails"
import FormAntdModal from "components/accounting/FormAntdModal"
import DetailsToolbar from "components/accounting/DetailsToolbar"
import { useDetailsColumns } from "components/accounting/useDetailsColumns"
import DetailsCardList from "components/accounting/DetailsCardList"
import CustomPagination from "components/common/CustomPagination"

import moment from "moment"

import { useDispatch, useSelector } from "react-redux"
import { collectionAnySelector, collectionSelector } from "store/selectors"
import { cleanExpense, deleteExpense, getExpense, getAccountingTripList, getLabels } from "store/accounting"

import { STATS_NAMES, FILTER_PARAM_KEYS, PAYMENT_TYPE_OPTIONS, BOOKING_TYPE_OPTIONS, TRANSACTION_TYPE_OPTIONS } from "constants/accounting"
import { DATE_FORMAT } from "modules/datepicker/constants"
import styles from "./Details.module.css"

const EMPTY_FILTERS = Object.fromEntries(FILTER_PARAM_KEYS.map((k) => [k, []]))

const filtersFromParams = (p) => Object.fromEntries(FILTER_PARAM_KEYS.map((k) => [k, p[k] ? String(p[k]).split(",").filter(Boolean) : []]))

function getDateRangeForPeriod(date, range) {
  const m = moment(date, DATE_FORMAT)
  if (range === "day") return { gteq: m.format(DATE_FORMAT), lteq: m.format(DATE_FORMAT) }
  if (range === "week") return { gteq: m.clone().startOf("week").format(DATE_FORMAT), lteq: m.clone().endOf("week").format(DATE_FORMAT) }
  return { gteq: m.clone().startOf("month").format(DATE_FORMAT), lteq: m.clone().endOf("month").format(DATE_FORMAT) }
}

function itemMatchesSearch(item, term) {
  if (
    String(item.displayed_name || "")
      .toLowerCase()
      .includes(term)
  )
    return true
  return (item.line_items || []).some(
    (li) =>
      String(li.name || "")
        .toLowerCase()
        .includes(term) ||
      String(li.category?.name || "")
        .toLowerCase()
        .includes(term)
  )
}

function buildFilterChips(activeFilterKeys, params, filterOptions) {
  const chips = []
  for (const key of activeFilterKeys) {
    const paramValue = params[key]
    if (!paramValue) continue
    const values = String(paramValue).split(",").filter(Boolean)
    const options = filterOptions[key] || []
    for (const val of values) {
      const option = options.find((o) => o.value === val)
      chips.push({ chipKey: `${key}:${val}`, filterKey: key, value: val, label: option?.label || val })
    }
  }
  return chips
}

function dispatchFilterBatch(batch, onChangeFiltersBatch, onChangeFilters) {
  if (onChangeFiltersBatch) {
    onChangeFiltersBatch(batch)
  } else {
    Object.entries(batch).forEach(([name, value]) => onChangeFilters?.({ target: { name, value } }))
  }
}

function buildTipPaginationMeta(tipData, params) {
  const totalCount = tipData.total_count || 0
  const currentPage = Number(params.page) || 1
  const currentPageSize = Number(params.per_page) || 10
  const totalPages = Math.ceil(totalCount / currentPageSize) || 1
  return {
    page: currentPage,
    total_pages: totalPages,
    next_page: currentPage < totalPages ? currentPage + 1 : null,
    prev_page: currentPage > 1 ? currentPage - 1 : null
  }
}

function usePagination({
  isSales,
  isExpenses,
  salesPagination,
  expensePagination,
  onSalesPageChange,
  onSalesPageSizeChange,
  onExpensePageChange,
  onExpensePageSizeChange
}) {
  const salesPage = salesPagination.page ?? 1
  const salesPageSize = salesPagination.per_page ?? 10
  const expensePage = expensePagination.page ?? 1
  const expensePageSize = expensePagination.per_page ?? 10
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  let activePage
  if (isSales) activePage = salesPage
  else if (isExpenses) activePage = expensePage
  else activePage = page

  let activePageSize
  if (isSales) activePageSize = salesPageSize
  else if (isExpenses) activePageSize = expensePageSize
  else activePageSize = pageSize

  let handlePageChange
  if (isSales) handlePageChange = onSalesPageChange
  else if (isExpenses) handlePageChange = onExpensePageChange
  else handlePageChange = setPage

  let handlePageSizeChange
  if (isSales) handlePageSizeChange = onSalesPageSizeChange
  else if (isExpenses) handlePageSizeChange = onExpensePageSizeChange
  else
    handlePageSizeChange = (size) => {
      setPageSize(size)
      setPage(1)
    }

  return { page, setPage, pageSize, activePage, activePageSize, handlePageChange, handlePageSizeChange }
}

export default function Details({
  params = {},
  onChangeFilters,
  onChangeFiltersBatch,
  onSuccess,
  tipToolbarSlot,
  hideDateSwitcher = false,
  salesPagination = {},
  onSalesPageChange,
  onSalesPageSizeChange,
  expensePagination = {},
  onExpensePageChange,
  onExpensePageSizeChange
}) {
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const items = useSelector(collectionSelector("accounting"))
  const hasItems = useSelector(collectionAnySelector("accounting"))
  const accountingMeta = useSelector((state) => state.accounting.metadata)
  const currentExpense = useSelector((state) => state.accounting.expense)
  const tripList = useSelector((state) => state.accounting.tripList)
  const [isFilterVisible, setIsFilterVisible] = useState(false)
  const [draftFilters, setDraftFilters] = useState(() => filtersFromParams(params))
  const [editExpenseId, setEditExpenseId] = useState(null)
  const [deleteTargetId, setDeleteTargetId] = useState(null)
  const [searchInput, setSearchInput] = useState("")
  const [toolbarHeight, setToolbarHeight] = useState(0)
  const toolbarRef = useRef(null)
  const prevTipFiltersRef = useRef(null)
  const searchDebounceRef = useRef(null)
  const isMountedRef = useRef(false)
  const appliedDateRangeRef = useRef(null)

  const type = params.type || searchParams.get("type")
  const currentDate = params.date || moment().format(DATE_FORMAT)
  const currentRange = params.range || "month"
  const statsName = STATS_NAMES[type]
  const isSales = type === "sales"
  const isExpenses = type === "expenses"
  const isServerPaged = isSales || isExpenses

  const { page, setPage, pageSize, activePage, activePageSize, handlePageChange, handlePageSizeChange } = usePagination({
    isSales,
    isExpenses,
    salesPagination,
    expensePagination,
    onSalesPageChange,
    onSalesPageSizeChange,
    onExpensePageChange,
    onExpensePageSizeChange
  })

  // clear search input and reset date-range tracking when switching tabs
  useEffect(() => {
    setSearchInput("")
    appliedDateRangeRef.current = null
  }, [params.type]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (params?.type !== "sales" && params?.type !== "expenses") setPage(1)
  }, [searchInput, params?.type])

  const crewIdsKey = JSON.stringify(params["crew_ids[]"] ?? [])
  useEffect(() => {
    const prev = prevTipFiltersRef.current
    const crewIds = params["crew_ids[]"] ?? []
    const next = { dateGteq: params.date_gteq, dateLteq: params.date_lteq, crewIds, includeUnassigned: params.include_unassigned }
    prevTipFiltersRef.current = next
    // skip on mount, and skip when include_unassigned is being initialized from undefined
    if (prev === null) return
    if (
      prev.includeUnassigned === undefined &&
      next.includeUnassigned !== undefined &&
      prev.dateGteq === next.dateGteq &&
      prev.dateLteq === next.dateLteq &&
      JSON.stringify(prev.crewIds ?? []) === JSON.stringify(next.crewIds)
    )
      return
    if (params.type === "tip_report") onChangeFilters({ target: { name: "page", value: 1 } })
  }, [params.date_gteq, params.date_lteq, crewIdsKey, params.include_unassigned]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = toolbarRef.current
    if (!el) return
    const observer = new ResizeObserver(() => setToolbarHeight(el.offsetHeight))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const expenseCategories = useSelector((state) => state.accounting.metadata?.expense_categories || [])
  const expenseTags = useSelector((state) => state.accounting.metadata?.labels || [])

  const activeFilterKeys = useMemo(
    () => (isSales ? ["payment_type", "booking_type", "transaction_type", "experience"] : ["category_id", "label_ids"]),
    [isSales]
  )
  const activeFilterCount = activeFilterKeys.filter((key) => Boolean(params[key])).length

  const experienceOptions = useMemo(
    () => tripList.map((trip) => ({ value: String(trip.id), label: trip.name || trip.title || String(trip.id) })),
    [tripList]
  )

  const categoryOptions = useMemo(() => expenseCategories.map((c) => ({ value: String(c.id), label: c.name })), [expenseCategories])

  const tagOptions = useMemo(() => expenseTags.map((tag) => ({ value: String(tag.id), label: tag.name })), [expenseTags])

  useEffect(() => {
    dispatch(getAccountingTripList())
    dispatch(getLabels())
  }, [dispatch])

  useEffect(() => () => clearTimeout(searchDebounceRef.current), [])

  const handleFilterOpenChange = useCallback(
    (open) => {
      if (open) setDraftFilters(filtersFromParams(params))
      setIsFilterVisible(open)
    },
    [params]
  )

  const handleApplyFilters = useCallback(() => {
    const batch = Object.fromEntries(
      FILTER_PARAM_KEYS.map((name) => {
        const arr = draftFilters[name] || []
        return [name, arr.length ? arr.join(",") : null]
      })
    )
    dispatchFilterBatch(batch, onChangeFiltersBatch, onChangeFilters)
    setIsFilterVisible(false)
  }, [draftFilters, onChangeFiltersBatch, onChangeFilters])

  const handleCancelFilters = useCallback(() => {
    setDraftFilters(filtersFromParams(params))
    setIsFilterVisible(false)
  }, [params])

  const handleResetFilters = useCallback(() => {
    setDraftFilters(EMPTY_FILTERS)
    const batch = Object.fromEntries(FILTER_PARAM_KEYS.map((name) => [name, null]))
    dispatchFilterBatch(batch, onChangeFiltersBatch, onChangeFilters)
    setIsFilterVisible(false)
  }, [onChangeFiltersBatch, onChangeFilters])

  const translatedPaymentTypeOptions = useMemo(
    () => PAYMENT_TYPE_OPTIONS.map((o) => ({ ...o, label: t(`accounting.payment_types.${o.value}`) })),
    [t]
  )
  const translatedBookingTypeOptions = useMemo(
    () => BOOKING_TYPE_OPTIONS.map((o) => ({ ...o, label: t(`accounting.booking_types.${o.value}`) })),
    [t]
  )
  const translatedTransactionTypeOptions = useMemo(
    () => TRANSACTION_TYPE_OPTIONS.map((o) => ({ ...o, label: t(`accounting.transaction_types.${o.value}`) })),
    [t]
  )

  const appliedFilterChips = useMemo(() => {
    const filterOptions = {
      payment_type: translatedPaymentTypeOptions,
      booking_type: translatedBookingTypeOptions,
      transaction_type: translatedTransactionTypeOptions,
      experience: experienceOptions,
      category_id: categoryOptions,
      label_ids: tagOptions
    }
    return buildFilterChips(activeFilterKeys, params, filterOptions)
  }, [
    params,
    activeFilterKeys,
    translatedPaymentTypeOptions,
    translatedBookingTypeOptions,
    translatedTransactionTypeOptions,
    experienceOptions,
    categoryOptions
  ])

  const handleRemoveChip = useCallback(
    (filterKey, value) => {
      const paramValue = params[filterKey] || ""
      const remaining = String(paramValue)
        .split(",")
        .filter((v) => v && v !== value)
      onChangeFilters({ target: { name: filterKey, value: remaining.length ? remaining.join(",") : null } })
    },
    [params, onChangeFilters]
  )

  const applyDateRange = useCallback(
    (date, range) => {
      const { gteq, lteq } = getDateRangeForPeriod(date, range)
      dispatchFilterBatch({ date_gteq: gteq, date_lteq: lteq }, onChangeFiltersBatch, onChangeFilters)
    },
    [onChangeFilters, onChangeFiltersBatch]
  )

  useEffect(() => {
    if (isMountedRef.current) {
      if (currentRange === "custom") return
      const key = `${currentDate}|${currentRange}`
      if (appliedDateRangeRef.current === key) return
      appliedDateRangeRef.current = key
      applyDateRange(currentDate, currentRange)
    } else {
      isMountedRef.current = true
      appliedDateRangeRef.current = `${currentDate}|${currentRange}`
    }
  }, [currentDate, currentRange]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRangeChange = useCallback(
    (value) => {
      const today = moment().format(DATE_FORMAT)
      if (value === "custom") {
        onChangeFilters?.({ target: { name: "range", value } })
        onChangeFilters?.({ target: { name: "date", value: today } })
      } else {
        const { gteq, lteq } = getDateRangeForPeriod(today, value)
        appliedDateRangeRef.current = `${today}|${value}`
        dispatchFilterBatch({ range: value, date: today, date_gteq: gteq, date_lteq: lteq }, onChangeFiltersBatch, onChangeFilters)
      }
    },
    [onChangeFilters, onChangeFiltersBatch]
  )

  const handleChangeFiltersWithDate = useCallback(
    (e) => {
      if (e?.target?.name === "date" && currentRange !== "custom") {
        const newDate = e.target.value
        const { gteq, lteq } = getDateRangeForPeriod(newDate, currentRange)
        appliedDateRangeRef.current = `${newDate}|${currentRange}`
        dispatchFilterBatch({ date: newDate, date_gteq: gteq, date_lteq: lteq }, onChangeFiltersBatch, onChangeFilters)
      } else {
        onChangeFilters?.(e)
      }
    },
    [onChangeFilters, onChangeFiltersBatch, currentRange]
  )

  const handleSearchChange = useCallback(
    (value) => {
      setSearchInput(value)
      if (isServerPaged) {
        clearTimeout(searchDebounceRef.current)
        searchDebounceRef.current = setTimeout(() => {
          onChangeFilters({ target: { name: "search", value: value || null } })
        }, 400)
      }
    },
    [isServerPaged, onChangeFilters]
  )

  const searchedItems = useMemo(() => {
    const list = Array.isArray(items) ? items : []
    if (isServerPaged || !searchInput.trim()) return list
    const term = searchInput.toLowerCase()
    return list.filter((item) => itemMatchesSearch(item, term))
  }, [items, searchInput, isServerPaged])

  const handleEdit = useCallback(
    (record) => {
      dispatch(getExpense(record.id))
      setEditExpenseId(record.id)
    },
    [dispatch]
  )

  const handleDelete = useCallback((record) => setDeleteTargetId(record.id), [])

  const handleCloseEditModal = useCallback(() => {
    setEditExpenseId(null)
    dispatch(cleanExpense())
  }, [dispatch])

  const handleDeleteConfirm = useCallback(() => {
    dispatch(deleteExpense(deleteTargetId))
    setDeleteTargetId(null)
  }, [dispatch, deleteTargetId])

  const { columns } = useDetailsColumns({ isSales, onEdit: handleEdit, onDelete: handleDelete })

  const screens = Grid.useBreakpoint()
  const isDesktop = screens.lg

  const serverPagination = accountingMeta?.pagination || {}
  const serverTotal = serverPagination.total_results || 0
  const serverTotalPages = serverPagination.total_pages || Math.ceil(serverTotal / activePageSize) || 1
  const serverNextPage = serverPagination.next_page ?? (activePage < serverTotalPages ? activePage + 1 : null)
  const serverPrevPage = serverPagination.prev_page ?? (activePage > 1 ? activePage - 1 : null)
  const serverPaginationMeta = {
    page: activePage,
    total_pages: serverTotalPages,
    next_page: serverNextPage,
    prev_page: serverPrevPage
  }

  const clientTotal = searchedItems.length
  const clientTotalPages = Math.ceil(clientTotal / pageSize) || 1
  const clientPaginationMeta = {
    page,
    total_pages: clientTotalPages,
    next_page: page < clientTotalPages ? page + 1 : null,
    prev_page: page > 1 ? page - 1 : null
  }

  const total = isServerPaged ? serverTotal : clientTotal
  const paginationMeta = isServerPaged ? serverPaginationMeta : clientPaginationMeta
  const pagedItems = useMemo(
    () => (isServerPaged ? searchedItems : searchedItems.slice((page - 1) * pageSize, page * pageSize)),
    [isServerPaged, searchedItems, page, pageSize]
  )

  const pagedTableData = useMemo(() => {
    if (isSales) return pagedItems
    return pagedItems.flatMap((expense) => {
      const lineItems = expense.line_items || []
      if (lineItems.length === 0) return [{ ...expense, _lineItem: null, _rowSpan: 1, _key: String(expense.id) }]
      return lineItems.map((lineItem, idx) => ({
        ...expense,
        _lineItem: lineItem,
        _rowSpan: idx === 0 ? lineItems.length : 0,
        _isLastLineItem: idx === lineItems.length - 1,
        _key: `${expense.id}-${idx}`
      }))
    })
  }, [isSales, pagedItems])

  const emptyText = useMemo(() => {
    const isFiltered = activeFilterCount > 0 || searchInput.trim().length > 0
    if (hasItems && isFiltered) return t("accounting.details.empty.no_results")
    return t("accounting.details.empty.no_data")
  }, [hasItems, activeFilterCount, searchInput, t])

  if (type === "tip_report") {
    const tipData = Array.isArray(items) || !items ? {} : items
    const tipPaginationMeta = buildTipPaginationMeta(tipData, params)
    return (
      <div className="mt-m">
        <div ref={toolbarRef} className={styles.stickyToolbar}>
          <DetailsToolbar
            currentDate={currentDate}
            currentRange={currentRange}
            isSales={false}
            searchTerm=""
            onSearchChange={() => {}}
            filterVisible={false}
            activeFilterCount={0}
            draftFilters={{}}
            setDraftFilters={() => {}}
            experienceOptions={[]}
            categoryOptions={[]}
            onFilterOpenChange={() => {}}
            onApplyFilters={() => {}}
            onCancelFilters={() => {}}
            onResetFilters={() => {}}
            onChangeFilters={handleChangeFiltersWithDate}
            onRangeChange={handleRangeChange}
            dateGteq={params.date_gteq}
            dateLteq={params.date_lteq}
            onCustomDateChange={(dates) => {
              onChangeFiltersBatch?.({ date_gteq: dates.gteq, date_lteq: dates.lteq })
            }}
            hideFilters
            leftAlignDate
            rightSlot={tipToolbarSlot}
          />
        </div>
        <TipsDetails
          tipReport={tipData.tip_report || []}
          totalTips={tipData.total_tips || "0.00"}
          totalCount={tipData.total_count || 0}
          crewBreakdown={tipData.crew_breakdown || []}
          unassignedTips={tipData.unassigned_tips || null}
          metadata={tipPaginationMeta}
          tipPage={Number(params.page) || 1}
          tipPageSize={Number(params.per_page) || 10}
          onTipPageChange={(p) => onChangeFilters({ target: { name: "page", value: p } })}
          onTipPageSizeChange={(ps) => {
            onChangeFilters({ target: { name: "per_page", value: ps } })
            onChangeFilters({ target: { name: "page", value: 1 } })
          }}
        />
      </div>
    )
  }

  return (
    <Row gutter={[16, 16]} className="mt-m">
      {!hideDateSwitcher && (
        <Col span={24}>
          <DetailsToolbar
            currentDate={currentDate}
            currentRange={currentRange}
            isSales={isSales}
            onSearchChange={() => {}}
            setDraftFilters={() => {}}
            onFilterOpenChange={() => {}}
            onApplyFilters={() => {}}
            onCancelFilters={() => {}}
            onResetFilters={() => {}}
            onChangeFilters={handleChangeFiltersWithDate}
            onRangeChange={handleRangeChange}
            dateGteq={params.date_gteq}
            dateLteq={params.date_lteq}
            onCustomDateChange={(dates) => {
              onChangeFiltersBatch?.({ date_gteq: dates.gteq, date_lteq: dates.lteq })
            }}
            hideFilters
          />
        </Col>
      )}
      <Col span={24} lg={12}>
        <Widget
          stats_name={statsName}
          isCurrency
          currentDate={currentDate}
          currentRange={currentRange}
          dateGteq={params.date_gteq}
          dateLteq={params.date_lteq}
        />
      </Col>
      <Col span={24} lg={12}>
        <Widget
          stats_name="booking"
          currentDate={currentDate}
          currentRange={currentRange}
          dateGteq={params.date_gteq}
          dateLteq={params.date_lteq}
        />
      </Col>
      <Col span={24}>
        <div ref={toolbarRef} className={styles.stickyToolbar}>
          <DetailsToolbar
            currentDate={currentDate}
            currentRange={currentRange}
            isSales={isSales}
            searchTerm={searchInput}
            onSearchChange={handleSearchChange}
            filterVisible={isFilterVisible}
            activeFilterCount={activeFilterCount}
            draftFilters={draftFilters}
            setDraftFilters={setDraftFilters}
            paymentTypeOptions={translatedPaymentTypeOptions}
            bookingTypeOptions={translatedBookingTypeOptions}
            transactionTypeOptions={translatedTransactionTypeOptions}
            experienceOptions={experienceOptions}
            categoryOptions={categoryOptions}
            tagOptions={tagOptions}
            onFilterOpenChange={handleFilterOpenChange}
            onApplyFilters={handleApplyFilters}
            onCancelFilters={handleCancelFilters}
            onResetFilters={handleResetFilters}
            onChangeFilters={handleChangeFiltersWithDate}
            onRangeChange={handleRangeChange}
            dateGteq={params.date_gteq}
            dateLteq={params.date_lteq}
            onCustomDateChange={(dates) => {
              onChangeFiltersBatch?.({ date_gteq: dates.gteq, date_lteq: dates.lteq })
            }}
            hideDateSwitcher
          />

          {appliedFilterChips.length > 0 && (
            <Flex gap={8} wrap justify="flex-end" className="mt-s">
              {appliedFilterChips.map(({ chipKey, filterKey, value, label }) => (
                <Tag
                  className={`m-0 ${styles.filterChip}`}
                  key={chipKey}
                  closable
                  onClose={() => handleRemoveChip(filterKey, value)}
                >
                  {label}
                </Tag>
              ))}
            </Flex>
          )}
        </div>
        {isDesktop ? (
          <DataTable
            dataSource={pagedTableData}
            rowKey={isSales ? "id" : "_key"}
            pagination={false}
            columns={columns}
            locale={{ emptyText }}
            scroll={{ x: "max-content" }}
            sticky={{ offsetHeader: toolbarHeight }}
          />
        ) : (
          <DetailsCardList items={pagedItems} isSales={isSales} onEdit={handleEdit} onDelete={handleDelete} />
        )}
        <CustomPagination
          metadata={paginationMeta}
          pageSize={activePageSize}
          total={total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </Col>

      <Modal
        open={!!deleteTargetId}
        title={t("expense.delete_title")}
        okText={t("expense.delete")}
        okButtonProps={{ danger: true }}
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteTargetId(null)}
      >
        <Typography.Text>{t("accounting.details.delete_description")}</Typography.Text>
      </Modal>

      {editExpenseId && (
        <FormAntdModal
          visible={!!editExpenseId}
          setVisible={handleCloseEditModal}
          expenseId={editExpenseId}
          expense={currentExpense}
          onClose={handleCloseEditModal}
          onSuccess={onSuccess}
        />
      )}
    </Row>
  )
}

Details.propTypes = {
  params: PropTypes.shape({
    type: PropTypes.string,
    date: PropTypes.string,
    range: PropTypes.string,
    payment_type: PropTypes.string,
    booking_type: PropTypes.string,
    transaction_type: PropTypes.string,
    experience: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    category_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  onChangeFilters: PropTypes.func,
  onChangeFiltersBatch: PropTypes.func,
  onSuccess: PropTypes.func,
  tipToolbarSlot: PropTypes.node,
  hideDateSwitcher: PropTypes.bool,
  salesPagination: PropTypes.shape({ page: PropTypes.number, per_page: PropTypes.number }),
  onSalesPageChange: PropTypes.func,
  onSalesPageSizeChange: PropTypes.func,
  expensePagination: PropTypes.shape({ page: PropTypes.number, per_page: PropTypes.number }),
  onExpensePageChange: PropTypes.func,
  onExpensePageSizeChange: PropTypes.func
}
